import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Person } from '../types/person';
import { diariesApi, peopleApi } from '../api';
import PersonForm from './PersonForm';
import RelationshipGraph from './RelationshipGraph';

interface DashboardData {
  social: any;
  score_components: { label: string; value: number; max: number; color: string }[];
  attention_list: { person_id: number; person_name: string; person_color: string; days_ago: number; urgency: string }[];
  analytics: {
    type_distribution: { type: string; count: number; pct: number }[];
    avg_strength: number;
    most_active: string | null;
    longest_silent: string | null;
    total_interactions_30d: number;
  } | null;
  birthdays: { person_id: number; person_name: string; person_color: string; date: string; days_until: number }[];
  diary_stats: Record<number, { last_date: string | null; diary_count: number; days_ago: number | null }>;
}

interface DiaryEntry {
  id: number;
  person_id?: number | null;
  content: string;
  date?: string;
  created_at?: string;
  diary_type?: string;
  person_name?: string;
  person_color?: string;
}

interface PeopleListProps {
  people: Person[];
  onPersonSelect: (personId: number) => void;
  onPersonCreated: () => void;
  onRefresh: () => void;
  onBackHome: () => void;
}

type SortMode = 'created' | 'name' | 'last_interaction' | 'interaction_count';
type WorkbenchTab = 'workbench' | 'people' | 'records' | 'graph' | 'analysis';

const RECORD_LABELS: Record<string, { label: string; tone: string }> = {
  note: { label: '日常交流', tone: 'blue' },
  mood: { label: '情绪记录', tone: 'purple' },
  fitness: { label: '生活相关', tone: 'green' },
  tech: { label: '工作相关', tone: 'orange' },
  grudge: { label: '重要记录', tone: 'green' },
  gratitude: { label: '正向记录', tone: 'green' },
};

const QUALITY_BUCKETS = [
  { label: '很亲密', range: '80-100', min: 80, color: '#41b95f' },
  { label: '亲密', range: '60-79', min: 60, color: '#3b82f6' },
  { label: '普通', range: '40-59', min: 40, color: '#f4b83f' },
  { label: '一般', range: '20-39', min: 20, color: '#f97316' },
  { label: '疏远', range: '0-19', min: 0, color: '#e5e7eb' },
];

const formatDateLabel = (value?: string) => {
  if (!value) return '最近';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '最近';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diff = Math.round((today - day) / 86400000);
  const time = date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (diff === 0) return `今天 ${time}`;
  if (diff === 1) return `昨天 ${time}`;
  if (diff < 7) return `${diff}天前 ${time}`;
  return `${date.getMonth() + 1}月${date.getDate()}日 ${time}`;
};

const clampQuality = (person: Person) => {
  const friendly = Number(person.attributes?.友好度);
  const trust = Number(person.attributes?.信任度);
  const level = Number(person.level);
  const values = [friendly, trust].filter(v => Number.isFinite(v));
  if (values.length) return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  if (Number.isFinite(level)) return Math.min(100, Math.max(20, level * 18 + 28));
  return 50;
};

const initials = (name: string) => name.trim().charAt(0) || '?';

const PeopleList: React.FC<PeopleListProps> = ({
  people: rawPeople,
  onPersonSelect,
  onPersonCreated,
  onRefresh,
  onBackHome,
}) => {
  const people = useMemo(() => (Array.isArray(rawPeople) ? rawPeople : []), [rawPeople]);
  const [activeTab, setActiveTab] = useState<WorkbenchTab>('workbench');
  const [showForm, setShowForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(people[0]?.id ?? null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [sortMode, setSortMode] = useState<SortMode>('last_interaction');
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [diaries, setDiaries] = useState<DiaryEntry[]>([]);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [batchTag, setBatchTag] = useState('');

  const loadDashboard = useCallback(async () => {
    try {
      const res = await peopleApi.getDashboard();
      setDashboard(res.data);
    } catch (err) {
      console.error('加载仪表板失败:', err);
    }
  }, []);

  const loadDiaries = useCallback(async () => {
    try {
      const res = await diariesApi.getAll();
      const entries = Array.isArray(res.data) ? res.data : [];
      setDiaries(entries);
    } catch (err) {
      console.error('加载最近记事失败:', err);
      setDiaries([]);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    loadDiaries();
  }, [loadDashboard, loadDiaries]);

  useEffect(() => {
    if (!selectedPersonId && people.length > 0) setSelectedPersonId(people[0].id);
  }, [people, selectedPersonId]);

  const peopleMap = useMemo(() => new Map(people.map(p => [p.id, p])), [people]);
  const selectedPerson = people.find(p => p.id === selectedPersonId) || people[0] || null;
  const analytics = dashboard?.analytics;
  const socialScore = Number(dashboard?.social?.social ?? analytics?.avg_strength ?? 0);

  const relationshipTypes = useMemo(() => {
    const types = new Set<string>();
    people.forEach(p => { if (p.relationship_type) types.add(p.relationship_type); });
    return Array.from(types);
  }, [people]);

  const filteredAndSorted = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const stats = dashboard?.diary_stats || {};
    const result = people.filter(p => {
      const matchesSearch = !query ||
        p.name.toLowerCase().includes(query) ||
        (p.relationship_type || '').toLowerCase().includes(query) ||
        (p.notes || '').toLowerCase().includes(query) ||
        (p.tags || []).some(t => t.toLowerCase().includes(query));
      const matchesFilter = filterType === 'all' || p.relationship_type === filterType;
      return matchesSearch && matchesFilter;
    });

    result.sort((a, b) => {
      if (sortMode === 'name') return a.name.localeCompare(b.name, 'zh');
      if (sortMode === 'interaction_count') return (stats[b.id]?.diary_count || 0) - (stats[a.id]?.diary_count || 0);
      if (sortMode === 'last_interaction') {
        const da = stats[a.id]?.days_ago;
        const db = stats[b.id]?.days_ago;
        if (da == null && db == null) return 0;
        if (da == null) return 1;
        if (db == null) return -1;
        return da - db;
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    return result;
  }, [dashboard, filterType, people, searchQuery, sortMode]);

  const allEntries = useMemo(() => {
    return diaries
      .map(entry => {
        const person = entry.person_id ? peopleMap.get(entry.person_id) : undefined;
        return {
          ...entry,
          person_name: entry.person_name || person?.name || '未关联人员',
          person_color: entry.person_color || person?.color || '#3b82f6',
        };
      })
      .sort((a, b) => new Date(b.date || b.created_at || 0).getTime() - new Date(a.date || a.created_at || 0).getTime());
  }, [diaries, peopleMap]);

  const selectedEntries = useMemo(() => {
    if (!selectedPerson) return allEntries.slice(0, 8);
    return allEntries.filter(entry => entry.person_id === selectedPerson.id).slice(0, 8);
  }, [allEntries, selectedPerson]);

  const associatedEntries = useMemo(() => {
    return allEntries.filter(entry => entry.person_id && peopleMap.has(entry.person_id));
  }, [allEntries, peopleMap]);

  const selectedDiaryCount = useMemo(() => {
    if (!selectedPerson) return allEntries.length;
    return allEntries.filter(entry => entry.person_id === selectedPerson.id).length;
  }, [allEntries, selectedPerson]);

  const selectedDaysAgo = selectedPerson ? dashboard?.diary_stats?.[selectedPerson.id]?.days_ago : null;
  const selectedQuality = selectedPerson ? clampQuality(selectedPerson) : Math.round(socialScore || 0);
  const selectedTags = selectedPerson?.tags || [];
  const selectedOverviewItems = selectedPerson ? [
    { label: '关系类型', value: selectedPerson.relationship_type || '未分类', color: '#8b5cf6' },
    { label: '关系质量', value: `${selectedQuality}`, color: selectedQuality >= 60 ? '#41b95f' : selectedQuality >= 40 ? '#f4b83f' : '#f97316' },
    { label: '关联记事', value: `${selectedDiaryCount}`, color: '#3b82f6' },
    { label: '最近互动', value: selectedDaysAgo == null ? '暂无' : selectedDaysAgo === 0 ? '今天' : `${selectedDaysAgo}天前`, color: '#22c1d6' },
    { label: '标签数量', value: `${selectedTags.length}`, color: '#f59e0b' },
  ] : [];

  const distribution = useMemo(() => {
    const counts = new Map<string, number>();
    people.forEach(p => counts.set(p.relationship_type || '未分类', (counts.get(p.relationship_type || '未分类') || 0) + 1));
    return Array.from(counts.entries()).map(([type, count], index) => ({
      type,
      count,
      color: ['#8b5cf6', '#3b82f6', '#41b95f', '#f59e0b', '#22c1d6', '#ef4444'][index % 6],
    }));
  }, [people]);

  const qualityDistribution = useMemo(() => {
    return QUALITY_BUCKETS.map((bucket, index) => {
      const next = QUALITY_BUCKETS[index - 1]?.min ?? 101;
      const count = people.filter(p => {
        const quality = clampQuality(p);
        return quality >= bucket.min && quality < next;
      }).length;
      return { ...bucket, count };
    });
  }, [people]);

  const handleCreateClick = () => { setEditingPerson(null); setShowForm(true); };
  const handleFormClose = () => { setShowForm(false); setEditingPerson(null); };
  const handleFormSubmit = () => {
    handleFormClose();
    setTimeout(() => {
      onPersonCreated();
      loadDashboard();
    }, 100);
  };

  const handleQuickRecord = async () => {
    const target = selectedPerson || people[0];
    if (!target) {
      handleCreateClick();
      return;
    }
    const note = window.prompt(`给 ${target.name} 添加一条记事`, '');
    if (note === null) return;
    try {
      await peopleApi.quickContact(target.id, note.trim() || undefined);
      await Promise.all([loadDashboard(), loadDiaries()]);
      onRefresh();
    } catch (err: any) {
      alert('添加记事失败: ' + (err.response?.data?.detail || err.message));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBatchDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`确定删除选中的 ${selectedIds.size} 个人员？`)) return;
    try {
      await Promise.all(Array.from(selectedIds).map(id => peopleApi.delete(id)));
      setSelectedIds(new Set());
      setBatchMode(false);
      onRefresh();
      loadDashboard();
    } catch {
      alert('删除失败');
    }
  };

  const handleBatchAddTag = async () => {
    if (!batchTag.trim() || !selectedIds.size) return;
    try {
      for (const id of Array.from(selectedIds)) {
        const p = people.find(pp => pp.id === id);
        if (!p) continue;
        const tags = [...(p.tags || [])];
        if (!tags.includes(batchTag.trim())) {
          tags.push(batchTag.trim());
          await peopleApi.update(id, { tags });
        }
      }
      setBatchTag('');
      setSelectedIds(new Set());
      setBatchMode(false);
      onRefresh();
    } catch {
      alert('操作失败');
    }
  };

  const activeRecordList = activeTab === 'records' ? associatedEntries : selectedEntries;

  return (
    <div className="rel-workbench">
      <header className="rel-header">
        <div className="rel-brand">
          <button className="rel-back-btn" onClick={onBackHome} aria-label="返回主页">
            <span>‹</span>
            返回
          </button>
          <div className="rel-brand-icon">缘</div>
          <div>
            <h1>缘 · 人际关系</h1>
            <p>看见连接，照顾重要的人</p>
          </div>
        </div>
        <nav className="rel-tabs" aria-label="人际关系视图">
          {[
            ['workbench', '工作台'],
            ['people', '人员'],
            ['records', '记事'],
            ['graph', '图谱'],
            ['analysis', '分析'],
          ].map(([key, label]) => (
            <button key={key} className={activeTab === key ? 'active' : ''} onClick={() => setActiveTab(key as WorkbenchTab)}>
              {label}
            </button>
          ))}
        </nav>
        <div className="rel-header-actions">
          <button className="rel-primary-btn" onClick={handleCreateClick}>+ 添加人员</button>
          <button className="rel-success-btn" onClick={handleQuickRecord}>+ 添加记事</button>
        </div>
      </header>

      <section className="rel-pulse-strip" aria-label="关系概览">
        <div>
          <span>关系网络</span>
          <strong>{people.length}</strong>
          <small>位联系人</small>
        </div>
        <div>
          <span>近 30 天</span>
          <strong>{analytics?.total_interactions_30d ?? 0}</strong>
          <small>次互动</small>
        </div>
        <div>
          <span>需要关心</span>
          <strong>{dashboard?.attention_list?.length ?? 0}</strong>
          <small>个提醒</small>
        </div>
        <div className="accent">
          <span>缘维度</span>
          <strong>{Math.round(socialScore || 0)}</strong>
          <small>连接指数</small>
        </div>
      </section>

      {activeTab === 'graph' ? (
        <main className="rel-graph-screen">
          <RelationshipGraph people={people} onPersonSelect={onPersonSelect} onRefresh={onRefresh} />
        </main>
      ) : activeTab === 'people' ? (
        <main className="rel-directory-screen">
          <section className="rel-card rel-people-directory-card">
            <div className="rel-record-head">
              <div>
                <h2>全部联系人</h2>
                <p>按关系类型、互动频率和关系质量管理联系人</p>
              </div>
              <button className="rel-link-btn" onClick={handleCreateClick}>+ 添加人员</button>
            </div>
            <div className="rel-directory-toolbar">
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="搜索姓名、关系、备注或标签"
              />
              <select value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="all">全部关系类型</option>
                {relationshipTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
              <select value={sortMode} onChange={e => setSortMode(e.target.value as SortMode)}>
                <option value="last_interaction">最近互动</option>
                <option value="interaction_count">互动最多</option>
                <option value="created">最近创建</option>
                <option value="name">姓名</option>
              </select>
              <button className={batchMode ? 'active' : ''} onClick={() => { setBatchMode(!batchMode); setSelectedIds(new Set()); }}>
                {batchMode ? '完成' : '批量管理'}
              </button>
            </div>
            <div className="rel-contact-table">
              <div className="rel-contact-head">
                <span>联系人</span>
                <span>关系类型</span>
                <span>最近互动</span>
                <span>记事</span>
                <span>质量</span>
              </div>
              {filteredAndSorted.map(person => {
                const quality = clampQuality(person);
                const daysAgo = dashboard?.diary_stats?.[person.id]?.days_ago;
                const diaryCount = dashboard?.diary_stats?.[person.id]?.diary_count || 0;
                return (
                  <div
                    key={person.id}
                    className={`rel-contact-row ${selectedPerson?.id === person.id ? 'active' : ''}`}
                    onClick={() => batchMode ? toggleSelect(person.id) : setSelectedPersonId(person.id)}
                    onDoubleClick={() => onPersonSelect(person.id)}
                  >
                    <div className="rel-contact-person">
                      {batchMode && <span className={`rel-check ${selectedIds.has(person.id) ? 'checked' : ''}`}>{selectedIds.has(person.id) ? '✓' : ''}</span>}
                      <div className="rel-avatar" style={{ background: person.color || '#e8f1ff', color: person.color ? '#fff' : '#3478f6' }}>
                        {person.avatar || initials(person.name)}
                      </div>
                      <div>
                        <strong>{person.name}</strong>
                        <em>{(person.tags || []).slice(0, 2).join(' · ') || `Lv.${person.level || 1}`}</em>
                      </div>
                    </div>
                    <span className="rel-contact-type" data-label="关系">{person.relationship_type || '未分类'}</span>
                    <span className="rel-contact-last" data-label="互动">{daysAgo == null ? '暂无互动' : daysAgo === 0 ? '今天' : `${daysAgo}天前`}</span>
                    <span className="rel-contact-count" data-label="记事">{diaryCount}</span>
                    <span className={`rel-score rel-contact-score ${quality >= 60 ? 'good' : quality >= 40 ? 'mid' : 'low'}`} data-label="质量">{quality}</span>
                  </div>
                );
              })}
            </div>
          </section>

          <aside className="rel-directory-side">
            <section className="rel-card rel-overview-card">
              <h2>关系类型分布</h2>
              <div className="rel-donut-wrap compact">
                <div
                  className="rel-donut"
                  style={{
                    background: `conic-gradient(${distribution.map((d, i) => {
                      const total = Math.max(1, people.length);
                      const start = distribution.slice(0, i).reduce((sum, item) => sum + item.count, 0) / total * 100;
                      const end = start + d.count / total * 100;
                      return `${d.color} ${start}% ${end}%`;
                    }).join(', ') || 'var(--web-chart-track, rgba(51,65,85,.72)) 0% 100%'})`,
                  }}
                >
                  <div><strong>{people.length}</strong><span>总人数</span></div>
                </div>
                <div className="rel-legend">
                  {distribution.slice(0, 8).map(item => (
                    <button key={item.type} className="rel-legend-btn" onClick={() => setFilterType(item.type)}>
                      <i style={{ background: item.color }} />
                      <span>{item.type}</span>
                      <strong>{item.count}</strong>
                    </button>
                  ))}
                </div>
              </div>
            </section>
            <section className="rel-card">
              <h2>关系质量分布</h2>
              <div className="rel-quality-list">
                {qualityDistribution.map(item => (
                  <div key={item.label} className="rel-quality-row">
                    <span>{item.label} <em>({item.range})</em></span>
                    <div><i style={{ width: `${people.length ? (item.count / people.length) * 100 : 0}%`, background: item.color }} /></div>
                    <strong>{item.count}</strong>
                  </div>
                ))}
              </div>
            </section>
          </aside>
        </main>
      ) : activeTab === 'records' ? (
        <main className="rel-records-screen">
          <section className="rel-card rel-record-panel rel-record-panel-wide">
            <div className="rel-record-head">
              <div>
                <h2>所有关联记事</h2>
                <p>仅显示已经关联到联系人档案的记事，共 {associatedEntries.length} 条</p>
              </div>
              <button className="rel-link-btn" onClick={handleQuickRecord}>+ 添加记事</button>
            </div>
            <div className="rel-timeline rel-timeline-wide">
              {activeRecordList.length === 0 ? (
                <div className="rel-empty">还没有与人员关联的记事。选择联系人并添加记事后会出现在这里。</div>
              ) : activeRecordList.map(entry => {
                const type = RECORD_LABELS[entry.diary_type || 'note'] || RECORD_LABELS.note;
                return (
                  <article key={entry.id} className="rel-entry">
                    <div className="rel-entry-dot" />
                    <div className="rel-entry-main">
                      <div className="rel-entry-top">
                        <span>{formatDateLabel(entry.date || entry.created_at)}</span>
                        <em className={`rel-tag ${type.tone}`}>{type.label}</em>
                      </div>
                      <h3>{entry.content.split('\n')[0].slice(0, 34) || '互动记录'}</h3>
                      <p>{entry.content.length > 120 ? `${entry.content.slice(0, 120)}...` : entry.content}</p>
                      <button className="rel-person-chip" onClick={() => entry.person_id && setSelectedPersonId(entry.person_id)}>
                        <span style={{ background: entry.person_color || '#3b82f6' }}>{initials(entry.person_name || '未')}</span>
                        {entry.person_name}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </main>
      ) : (
        <main className={`rel-layout ${activeTab === 'analysis' ? 'is-analysis' : ''}`}>
          <aside className="rel-card rel-people-panel">
            <div className="rel-panel-title">
              <h2>人员</h2>
              <span>{filteredAndSorted.length} 人</span>
            </div>
            <div className="rel-search-row">
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="搜索姓名、关系或标签"
              />
              <button aria-label="批量管理" className={batchMode ? 'active' : ''} onClick={() => { setBatchMode(!batchMode); setSelectedIds(new Set()); }}>
                筛
              </button>
            </div>
            <select className="rel-filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="all">全部标签</option>
              {relationshipTypes.map(type => <option key={type} value={type}>{type}</option>)}
            </select>
            <div className="rel-sort-row">
              <select value={sortMode} onChange={e => setSortMode(e.target.value as SortMode)}>
                <option value="last_interaction">最近互动</option>
                <option value="interaction_count">互动最多</option>
                <option value="created">最近创建</option>
                <option value="name">姓名</option>
              </select>
              <button className={batchMode ? 'active' : ''} onClick={() => { setBatchMode(!batchMode); setSelectedIds(new Set()); }}>
                {batchMode ? '完成' : '批量'}
              </button>
            </div>
            <div className="rel-person-list">
              {filteredAndSorted.map(person => {
                const quality = clampQuality(person);
                const daysAgo = dashboard?.diary_stats?.[person.id]?.days_ago;
                const active = selectedPerson?.id === person.id;
                return (
                  <div
                    key={person.id}
                    className={`rel-person-row ${active ? 'active' : ''}`}
                    onClick={() => batchMode ? toggleSelect(person.id) : setSelectedPersonId(person.id)}
                    onDoubleClick={() => onPersonSelect(person.id)}
                  >
                    {batchMode && <span className={`rel-check ${selectedIds.has(person.id) ? 'checked' : ''}`}>{selectedIds.has(person.id) ? '✓' : ''}</span>}
                    <div className="rel-avatar" style={{ background: person.color || '#e8f1ff', color: person.color ? '#fff' : '#3478f6' }}>
                      {person.avatar || initials(person.name)}
                    </div>
                    <div className="rel-person-copy">
                      <div className="rel-person-name">
                        <strong>{person.name}</strong>
                        <span>Lv.{person.level || 1}</span>
                      </div>
                      <div className="rel-person-meta">
                        <span>{person.relationship_type || '未分类'}</span>
                        <span>{daysAgo == null ? '暂无互动' : daysAgo === 0 ? '今天互动' : `${daysAgo}天前`}</span>
                      </div>
                    </div>
                    <span className={`rel-score ${quality >= 60 ? 'good' : quality >= 40 ? 'mid' : 'low'}`}>{quality}</span>
                  </div>
                );
              })}
            </div>
            <button className="rel-full-btn" onClick={() => setActiveTab('people')}>查看全部人员</button>
          </aside>

          <section className="rel-card rel-record-panel">
            <div className="rel-record-head">
              <div>
                <h2>{activeTab === 'analysis' ? '分析摘要' : `${selectedPerson?.name || '全部人员'}的最近记事`}</h2>
                <p>{selectedPerson ? `已自动筛选 ${selectedDiaryCount} 条关联记事` : '记录互动、跟进和关系变化'}</p>
              </div>
              <div className="rel-record-filters">
                {['全部', '今天', '最近7天', '仅我记录'].map((item, index) => (
                  <button key={item} className={index === 0 ? 'active' : ''}>{item}</button>
                ))}
              </div>
              <button className="rel-link-btn" onClick={handleQuickRecord}>+ 添加记事</button>
            </div>

            {activeTab === 'analysis' ? (
              <div className="rel-analysis-focus">
                <div className="rel-big-score">
                  <span>{Math.round(socialScore || 0)}</span>
                  <strong>综合社交力</strong>
                </div>
                <div className="rel-analysis-lines">
                  {(dashboard?.score_components || []).map(component => (
                    <div key={component.label} className="rel-analysis-line">
                      <span>{component.label}</span>
                      <div><i style={{ width: `${Math.min(100, (component.value / component.max) * 100)}%`, background: component.color }} /></div>
                      <strong>{component.value}/{component.max}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rel-timeline">
                {activeRecordList.length === 0 ? (
                  <div className="rel-empty">{selectedPerson ? `${selectedPerson.name} 暂无关联记事。添加一条互动记录后会出现在这里。` : '暂无记事。选择一个人员后添加第一条互动记录。'}</div>
                ) : activeRecordList.map(entry => {
                  const type = RECORD_LABELS[entry.diary_type || 'note'] || RECORD_LABELS.note;
                  return (
                    <article key={entry.id} className="rel-entry">
                      <div className="rel-entry-dot" />
                      <div className="rel-entry-main">
                        <div className="rel-entry-top">
                          <span>{formatDateLabel(entry.date || entry.created_at)}</span>
                          <em className={`rel-tag ${type.tone}`}>{type.label}</em>
                        </div>
                        <h3>{entry.content.split('\n')[0].slice(0, 28) || '互动记录'}</h3>
                        <p>{entry.content.length > 88 ? `${entry.content.slice(0, 88)}...` : entry.content}</p>
                        <button className="rel-person-chip" onClick={() => entry.person_id && setSelectedPersonId(entry.person_id)}>
                          <span style={{ background: entry.person_color || '#3b82f6' }}>{initials(entry.person_name || '未')}</span>
                          {entry.person_name}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
            <button className="rel-full-btn rel-record-more" onClick={() => setActiveTab('records')}>{selectedPerson ? `查看 ${selectedPerson.name} 的全部记事` : '查看全部记事'}</button>
          </section>

          <aside className="rel-side-stack">
            <section className="rel-card rel-overview-card">
              <h2>{selectedPerson ? `${selectedPerson.name} · 关系概览` : '关系概览'}</h2>
              <div className="rel-donut-wrap">
                <div
                  className="rel-donut"
                  style={{
                    background: selectedPerson
                      ? `conic-gradient(${selectedQuality >= 60 ? '#41b95f' : selectedQuality >= 40 ? '#f4b83f' : '#f97316'} 0 ${selectedQuality}%, var(--web-chart-track, rgba(51,65,85,.72)) ${selectedQuality}% 100%)`
                      : `conic-gradient(${distribution.map((d, i) => {
                        const total = Math.max(1, people.length);
                        const start = distribution.slice(0, i).reduce((sum, item) => sum + item.count, 0) / total * 100;
                        const end = start + d.count / total * 100;
                        return `${d.color} ${start}% ${end}%`;
                      }).join(', ') || 'var(--web-chart-track, rgba(51,65,85,.72)) 0% 100%'})`,
                  }}
                >
                  <div><strong>{selectedPerson ? selectedQuality : people.length}</strong><span>{selectedPerson ? '关系质量' : '总人数'}</span></div>
                </div>
                <div className="rel-legend">
                  {selectedPerson ? selectedOverviewItems.map(item => (
                    <div key={item.label}>
                      <i style={{ background: item.color }} />
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  )) : distribution.slice(0, 6).map(item => (
                    <div key={item.type}>
                      <i style={{ background: item.color }} />
                      <span>{item.type}</span>
                      <strong>{item.count}</strong>
                    </div>
                  ))}
                </div>
              </div>
              {selectedPerson?.notes && <p className="rel-person-summary">{selectedPerson.notes}</p>}
            </section>

            <section className="rel-card">
              <h2>关系质量分布</h2>
              <div className="rel-quality-list">
                {qualityDistribution.map(item => (
                  <div key={item.label} className="rel-quality-row">
                    <span>{item.label} <em>({item.range})</em></span>
                    <div><i style={{ width: `${people.length ? (item.count / people.length) * 100 : 0}%`, background: item.color }} /></div>
                    <strong>{item.count}</strong>
                  </div>
                ))}
              </div>
            </section>

            <section className="rel-card">
              <h2>快速操作</h2>
              <div className="rel-quick-list">
                <button onClick={() => setActiveTab('graph')}><strong>人物关系图谱</strong><span>可视化查看人物关系网络</span></button>
                <button onClick={() => setActiveTab('analysis')}><strong>关系分析报告</strong><span>生成关系分布和互动报告</span></button>
                <button onClick={() => setBatchMode(true)}><strong>批量管理人员</strong><span>添加标签或批量删除人员</span></button>
              </div>
            </section>

            <section className="rel-card">
              <h2>本周互动统计</h2>
              <div className="rel-week-stats">
                <div><strong>{selectedPerson ? 1 : new Set(allEntries.slice(0, 12).map(e => e.person_id).filter(Boolean)).size}</strong><span>{selectedPerson ? '当前人物' : '互动人数'}</span></div>
                <div><strong>{selectedPerson ? selectedDiaryCount : allEntries.slice(0, 8).length}</strong><span>{selectedPerson ? '关联记事' : '新增记事'}</span></div>
                <div><strong>{selectedPerson ? selectedQuality : Math.round(analytics?.avg_strength || socialScore || 0)}</strong><span>{selectedPerson ? '关系质量' : '平均关系强度'}</span></div>
              </div>
            </section>
          </aside>
        </main>
      )}

      {showForm && <PersonForm person={editingPerson} onClose={handleFormClose} onSubmit={handleFormSubmit} />}

      {batchMode && selectedIds.size > 0 && (
        <div className="rel-batch-bar">
          <span>已选 {selectedIds.size} 项</span>
          <input value={batchTag} onChange={e => setBatchTag(e.target.value)} placeholder="标签名" />
          <button onClick={handleBatchAddTag}>加标签</button>
          <button className="danger" onClick={handleBatchDelete}>删除</button>
        </div>
      )}

      <style>{`
        .rel-workbench {
          min-height:100vh;
          background:#f6f8fc;
          color:#162033;
          font-family:Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          letter-spacing:0;
          padding-bottom:0;
        }
        .rel-workbench button,
        .rel-workbench input,
        .rel-workbench select {
          font-family:inherit;
          letter-spacing:0;
        }
        .rel-header {
          min-height:102px;
          display:grid;
          grid-template-columns:minmax(260px, 1fr) auto minmax(260px, 1fr);
          align-items:center;
          gap:20px;
          padding:18px 24px;
          background:rgba(255,255,255,0.94);
          border-bottom:1px solid #e5eaf2;
          box-shadow:0 10px 30px rgba(30,64,175,0.04);
          position:sticky;
          top:0;
          z-index:20;
          backdrop-filter:blur(14px);
        }
        .rel-brand {
          display:flex;
          align-items:center;
          gap:12px;
          min-width:0;
        }
        .rel-back-btn {
          height:38px;
          border:1px solid #dfe5ef;
          border-radius:7px;
          background:#fff;
          color:#3a4658;
          display:inline-flex;
          align-items:center;
          gap:5px;
          padding:0 12px 0 9px;
          font-size:13px;
          font-weight:800;
          cursor:pointer;
          box-shadow:0 6px 14px rgba(15,23,42,0.04);
          flex-shrink:0;
        }
        .rel-back-btn:hover {
          border-color:#bcd2f7;
          color:#2f74f6;
          background:#f8fbff;
        }
        .rel-back-btn:active {
          transform:scale(0.98);
        }
        .rel-back-btn span {
          font-size:24px;
          line-height:1;
          margin-top:-2px;
        }
        .rel-brand-icon {
          width:48px;
          height:48px;
          border-radius:50%;
          background:linear-gradient(135deg, #dbeafe, #eef4ff);
          color:#2f74f6;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:900;
          font-size:20px;
          box-shadow:inset 0 0 0 1px #dce8ff;
        }
        .rel-brand h1 {
          margin:0;
          font-size:22px;
          line-height:1.15;
          color:#172033;
          font-weight:850;
        }
        .rel-brand p {
          margin:6px 0 0;
          color:#667085;
          font-size:14px;
        }
        .rel-tabs {
          display:flex;
          align-items:center;
          gap:20px;
          height:100%;
        }
        .rel-tabs button {
          height:62px;
          border:0;
          border-bottom:3px solid transparent;
          background:transparent;
          color:#657084;
          font-weight:750;
          font-size:14px;
          padding:0 2px;
          cursor:pointer;
          white-space:nowrap;
        }
        .rel-tabs button.active {
          color:#2f74f6;
          border-bottom-color:#2f74f6;
        }
        .rel-header-actions {
          display:flex;
          justify-content:flex-end;
          gap:10px;
        }
        .rel-primary-btn,
        .rel-success-btn {
          height:42px;
          border:0;
          border-radius:6px;
          padding:0 18px;
          color:white;
          font-size:14px;
          font-weight:800;
          cursor:pointer;
          box-shadow:0 10px 18px rgba(37,99,235,0.16);
        }
        .rel-primary-btn { background:#2f74f6; }
        .rel-success-btn { background:#31ad4d; box-shadow:0 10px 18px rgba(49,173,77,0.16); }
        .rel-layout {
          display:grid;
          grid-template-columns:330px minmax(420px, 1fr) 390px;
          gap:22px;
          padding:24px;
          max-width:1540px;
          margin:0 auto;
        }
        .rel-directory-screen,
        .rel-records-screen {
          display:grid;
          gap:22px;
          padding:24px;
          max-width:1540px;
          margin:0 auto;
        }
        .rel-directory-screen {
          grid-template-columns:minmax(640px, 1fr) 390px;
        }
        .rel-records-screen {
          grid-template-columns:minmax(0, 980px);
          justify-content:center;
        }
        .rel-card {
          background:#fff;
          border:1px solid #e5eaf2;
          border-radius:8px;
          box-shadow:0 10px 30px rgba(15,23,42,0.06);
        }
        .rel-people-directory-card,
        .rel-record-panel-wide {
          padding:16px;
          min-height:700px;
        }
        .rel-directory-toolbar {
          display:grid;
          grid-template-columns:minmax(220px, 1fr) 170px 150px 104px;
          gap:10px;
          padding:14px 0;
          border-bottom:1px solid #edf0f5;
          margin-bottom:8px;
        }
        .rel-directory-toolbar input,
        .rel-directory-toolbar select,
        .rel-directory-toolbar button {
          height:40px;
          border:1px solid #dfe5ef;
          border-radius:6px;
          background:#fff;
          color:#172033;
          font-size:14px;
          padding:0 12px;
          outline:none;
        }
        .rel-directory-toolbar button {
          color:#667085;
          font-weight:800;
          cursor:pointer;
        }
        .rel-directory-toolbar button.active {
          border-color:#2f74f6;
          color:#2f74f6;
          background:#edf4ff;
        }
        .rel-contact-table {
          display:flex;
          flex-direction:column;
          gap:6px;
        }
        .rel-contact-head,
        .rel-contact-row {
          display:grid;
          grid-template-columns:minmax(230px, 1.4fr) minmax(90px, 0.7fr) minmax(100px, 0.7fr) 64px 70px;
          align-items:center;
          gap:12px;
        }
        .rel-contact-head {
          min-height:36px;
          color:#667085;
          font-size:12px;
          font-weight:850;
          padding:0 12px;
          border-bottom:1px solid #edf0f5;
        }
        .rel-contact-row {
          min-height:68px;
          border:1px solid transparent;
          border-radius:7px;
          padding:8px 12px;
          color:#475467;
          font-size:14px;
          cursor:pointer;
        }
        .rel-contact-row:hover {
          background:#f8fbff;
          border-color:#e7eefb;
        }
        .rel-contact-row.active {
          background:#edf5ff;
          border-color:#9cc3ff;
        }
        .rel-contact-person {
          min-width:0;
          display:flex;
          align-items:center;
          gap:10px;
        }
        .rel-contact-person > div:last-child {
          min-width:0;
          display:flex;
          flex-direction:column;
        }
        .rel-contact-person strong {
          color:#172033;
          font-size:15px;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .rel-contact-person em {
          margin-top:3px;
          color:#667085;
          font-style:normal;
          font-size:12px;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .rel-directory-side {
          display:flex;
          flex-direction:column;
          gap:14px;
        }
        .rel-directory-side .rel-card {
          padding:18px;
        }
        .rel-record-panel-wide .rel-record-head {
          margin-bottom:4px;
        }
        .rel-timeline-wide {
          max-width:820px;
          margin:0 auto;
        }
        .rel-people-panel {
          padding:14px;
          align-self:start;
          position:sticky;
          top:126px;
          max-height:calc(100vh - 150px);
          display:flex;
          flex-direction:column;
        }
        .rel-panel-title,
        .rel-record-head {
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
        }
        .rel-panel-title h2,
        .rel-card h2,
        .rel-record-head h2 {
          margin:0;
          color:#182033;
          font-size:18px;
          font-weight:850;
        }
        .rel-panel-title span {
          color:#667085;
          font-size:14px;
        }
        .rel-search-row {
          display:grid;
          grid-template-columns:1fr 42px;
          gap:8px;
          margin:18px 0 10px;
        }
        .rel-search-row input,
        .rel-filter-select,
        .rel-sort-row select {
          height:40px;
          border:1px solid #dfe5ef;
          border-radius:6px;
          background:#fff;
          color:#172033;
          font-size:14px;
          padding:0 12px;
          outline:none;
        }
        .rel-search-row input:focus,
        .rel-filter-select:focus,
        .rel-sort-row select:focus {
          border-color:#93b7ff;
          box-shadow:0 0 0 3px rgba(47,116,246,0.1);
        }
        .rel-search-row button,
        .rel-sort-row button {
          border:1px solid #dfe5ef;
          border-radius:6px;
          background:#fff;
          color:#667085;
          font-weight:800;
          cursor:pointer;
        }
        .rel-search-row button.active,
        .rel-sort-row button.active {
          border-color:#2f74f6;
          color:#2f74f6;
          background:#edf4ff;
        }
        .rel-filter-select { width:100%; margin-bottom:10px; }
        .rel-sort-row {
          display:grid;
          grid-template-columns:1fr 68px;
          gap:8px;
          margin-bottom:10px;
        }
        .rel-person-list {
          display:flex;
          flex-direction:column;
          gap:8px;
          overflow:auto;
          padding-right:2px;
        }
        .rel-person-row {
          min-height:70px;
          display:flex;
          align-items:center;
          gap:12px;
          border:1px solid transparent;
          border-radius:6px;
          padding:9px 10px;
          cursor:pointer;
          background:#fff;
        }
        .rel-person-row:hover { background:#f8fbff; border-color:#e7eefb; }
        .rel-person-row.active {
          background:#edf5ff;
          border-color:#9cc3ff;
          box-shadow:inset 3px 0 0 #2f74f6;
        }
        .rel-check {
          width:18px;
          height:18px;
          border:1px solid #cbd5e1;
          border-radius:4px;
          display:flex;
          align-items:center;
          justify-content:center;
          color:white;
          font-size:12px;
          flex-shrink:0;
        }
        .rel-check.checked {
          background:#2f74f6;
          border-color:#2f74f6;
        }
        .rel-avatar {
          width:46px;
          height:46px;
          border-radius:50%;
          display:flex;
          align-items:center;
          justify-content:center;
          flex-shrink:0;
          font-size:20px;
          font-weight:850;
          box-shadow:inset 0 0 0 1px rgba(15,23,42,0.05);
        }
        .rel-person-copy {
          flex:1;
          min-width:0;
        }
        .rel-person-name {
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:8px;
          min-width:0;
        }
        .rel-person-name strong {
          color:#172033;
          font-size:16px;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .rel-person-name span {
          color:#667085;
          font-size:12px;
          flex-shrink:0;
        }
        .rel-person-meta {
          margin-top:5px;
          display:flex;
          align-items:center;
          gap:10px;
          color:#667085;
          font-size:13px;
          min-width:0;
        }
        .rel-person-meta span {
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .rel-score {
          min-width:32px;
          height:26px;
          border-radius:7px;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:850;
          font-size:14px;
          color:#667085;
          background:#f1f5f9;
        }
        .rel-score.good { color:#24a449; background:#dcf8df; }
        .rel-score.mid { color:#d58c00; background:#fff5d8; }
        .rel-score.low { color:#64748b; background:#eef2f7; }
        .rel-full-btn {
          margin-top:12px;
          height:38px;
          border:1px solid #e0e6ef;
          border-radius:6px;
          background:#fff;
          color:#3a4658;
          font-weight:700;
          cursor:pointer;
        }
        .rel-record-panel {
          padding:16px;
          min-height:700px;
        }
        .rel-record-head {
          align-items:flex-start;
          border-bottom:1px solid #edf0f5;
          padding-bottom:14px;
          margin-bottom:14px;
        }
        .rel-record-head p {
          margin:5px 0 0;
          color:#667085;
          font-size:13px;
        }
        .rel-record-filters {
          display:flex;
          gap:8px;
          flex-wrap:wrap;
        }
        .rel-record-filters button {
          height:32px;
          border:1px solid #dde5f0;
          border-radius:999px;
          background:#f8fafc;
          color:#667085;
          padding:0 15px;
          font-size:13px;
          font-weight:700;
          cursor:pointer;
        }
        .rel-record-filters button.active {
          background:#2f74f6;
          border-color:#2f74f6;
          color:white;
        }
        .rel-link-btn {
          border:0;
          background:transparent;
          color:#31ad4d;
          font-weight:800;
          cursor:pointer;
          white-space:nowrap;
        }
        .rel-timeline {
          position:relative;
          padding-left:36px;
        }
        .rel-timeline:before {
          content:"";
          position:absolute;
          left:14px;
          top:12px;
          bottom:12px;
          width:2px;
          background:#e6edf7;
        }
        .rel-entry {
          position:relative;
          padding:14px 0 20px;
          border-bottom:1px solid #edf0f5;
        }
        .rel-entry:last-child { border-bottom:0; }
        .rel-entry-dot {
          position:absolute;
          left:-28px;
          top:20px;
          width:14px;
          height:14px;
          border-radius:50%;
          background:#2f74f6;
          border:3px solid #e8f1ff;
          box-shadow:0 0 0 1px #b9d3ff;
        }
        .rel-entry-top {
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          color:#667085;
          font-size:14px;
        }
        .rel-entry h3 {
          margin:8px 0 6px;
          color:#172033;
          font-size:17px;
          font-weight:850;
        }
        .rel-entry p {
          margin:0;
          color:#5f6b7c;
          font-size:14px;
          line-height:1.65;
        }
        .rel-tag {
          border-radius:7px;
          padding:4px 9px;
          font-style:normal;
          font-size:12px;
          font-weight:800;
          white-space:nowrap;
        }
        .rel-tag.blue { color:#2f74f6; background:#e8f1ff; }
        .rel-tag.green { color:#20a34a; background:#e8f8ed; }
        .rel-tag.orange { color:#dc7d00; background:#fff2dd; }
        .rel-tag.purple { color:#8752e9; background:#f2eaff; }
        .rel-person-chip {
          margin-top:12px;
          border:1px solid #e1e8f2;
          background:#f8fafc;
          color:#3a4658;
          border-radius:999px;
          min-height:28px;
          padding:0 10px 0 4px;
          display:inline-flex;
          align-items:center;
          gap:7px;
          cursor:pointer;
          font-weight:700;
        }
        .rel-person-chip span {
          width:22px;
          height:22px;
          border-radius:50%;
          display:flex;
          align-items:center;
          justify-content:center;
          color:white;
          font-size:12px;
          font-weight:900;
        }
        .rel-side-stack {
          display:flex;
          flex-direction:column;
          gap:14px;
        }
        .rel-side-stack .rel-card {
          padding:18px;
        }
        .rel-donut-wrap {
          display:grid;
          grid-template-columns:178px 1fr;
          align-items:center;
          gap:16px;
          margin-top:14px;
        }
        .rel-donut {
          width:150px;
          height:150px;
          border-radius:50%;
          display:flex;
          align-items:center;
          justify-content:center;
          margin:auto;
        }
        .rel-donut > div {
          width:92px;
          height:92px;
          border-radius:50%;
          background:white;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          box-shadow:inset 0 0 0 1px #edf0f5;
        }
        .rel-donut strong {
          font-size:28px;
          color:#172033;
          line-height:1;
        }
        .rel-donut span {
          margin-top:6px;
          color:#667085;
          font-size:13px;
        }
        .rel-legend {
          display:flex;
          flex-direction:column;
          gap:10px;
        }
        .rel-legend div {
          display:grid;
          grid-template-columns:10px 1fr auto;
          align-items:center;
          gap:9px;
          color:#667085;
          font-size:14px;
        }
        .rel-legend i {
          width:8px;
          height:8px;
          border-radius:50%;
        }
        .rel-legend strong {
          color:#3a4658;
        }
        .rel-legend-btn {
          width:100%;
          display:grid;
          grid-template-columns:10px 1fr auto;
          align-items:center;
          gap:9px;
          border:0;
          background:transparent;
          color:#667085;
          font-size:14px;
          text-align:left;
          padding:0;
          cursor:pointer;
        }
        .rel-legend-btn:hover span {
          color:#2f74f6;
        }
        .rel-donut-wrap.compact {
          grid-template-columns:150px 1fr;
        }
        .rel-donut-wrap.compact .rel-donut {
          width:130px;
          height:130px;
        }
        .rel-donut-wrap.compact .rel-donut > div {
          width:80px;
          height:80px;
        }
        .rel-quality-list {
          display:flex;
          flex-direction:column;
          gap:13px;
          margin-top:16px;
        }
        .rel-quality-row {
          display:grid;
          grid-template-columns:102px 1fr 24px;
          align-items:center;
          gap:10px;
          color:#3a4658;
          font-size:14px;
        }
        .rel-quality-row em {
          color:#667085;
          font-style:normal;
        }
        .rel-quality-row div,
        .rel-analysis-line div {
          height:7px;
          border-radius:999px;
          background:#eef2f7;
          overflow:hidden;
        }
        .rel-quality-row i,
        .rel-analysis-line i {
          display:block;
          height:100%;
          border-radius:999px;
        }
        .rel-quick-list {
          display:flex;
          flex-direction:column;
          gap:10px;
          margin-top:14px;
        }
        .rel-quick-list button {
          min-height:58px;
          border:1px solid #e1e8f2;
          border-radius:7px;
          background:#fff;
          text-align:left;
          padding:10px 14px;
          cursor:pointer;
        }
        .rel-quick-list strong {
          display:block;
          color:#172033;
          font-size:14px;
        }
        .rel-quick-list span {
          display:block;
          color:#667085;
          font-size:13px;
          margin-top:3px;
        }
        .rel-week-stats {
          display:grid;
          grid-template-columns:repeat(3,1fr);
          text-align:center;
          margin-top:12px;
        }
        .rel-week-stats div + div {
          border-left:1px solid #edf0f5;
        }
        .rel-week-stats strong {
          display:block;
          color:#172033;
          font-size:28px;
          line-height:1;
          margin:10px 0 8px;
        }
        .rel-week-stats span {
          color:#667085;
          font-size:13px;
        }
        .rel-analysis-focus {
          display:grid;
          grid-template-columns:220px 1fr;
          gap:24px;
          align-items:center;
          padding:24px;
          background:#f8fbff;
          border:1px solid #e7eefb;
          border-radius:8px;
        }
        .rel-big-score {
          width:176px;
          height:176px;
          border-radius:50%;
          margin:auto;
          background:linear-gradient(135deg, #2f74f6, #41b95f);
          color:white;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
        }
        .rel-big-score span {
          font-size:48px;
          font-weight:900;
          line-height:1;
        }
        .rel-big-score strong {
          margin-top:8px;
          font-size:14px;
        }
        .rel-analysis-lines {
          display:flex;
          flex-direction:column;
          gap:14px;
        }
        .rel-analysis-line {
          display:grid;
          grid-template-columns:82px 1fr 54px;
          align-items:center;
          gap:10px;
          color:#3a4658;
          font-size:14px;
        }
        .rel-empty {
          border:1px dashed #dbe3ef;
          background:#f8fafc;
          color:#667085;
          border-radius:8px;
          padding:48px 20px;
          text-align:center;
        }
        .rel-graph-screen {
          padding:24px;
          background:#f6f8fc;
          min-height:100vh;
        }
        .rel-batch-bar {
          position:fixed;
          left:50%;
          bottom:calc(82px + var(--safe-area-bottom, 0px));
          transform:translateX(-50%);
          z-index:60;
          display:flex;
          align-items:center;
          gap:8px;
          background:#172033;
          color:white;
          border-radius:8px;
          padding:10px 12px;
          box-shadow:0 18px 50px rgba(15,23,42,0.24);
        }
        .rel-batch-bar input {
          height:32px;
          border:0;
          border-radius:6px;
          padding:0 10px;
        }
        .rel-batch-bar button {
          height:32px;
          border:0;
          border-radius:6px;
          padding:0 12px;
          background:#2f74f6;
          color:white;
          font-weight:800;
          cursor:pointer;
        }
        .rel-batch-bar button.danger {
          background:#ef4444;
        }
        @media (max-width:1180px) {
          .rel-header {
            grid-template-columns:1fr;
            align-items:start;
          }
          .rel-tabs {
            overflow-x:auto;
            height:auto;
            padding-bottom:2px;
          }
          .rel-tabs button {
            height:38px;
          }
          .rel-header-actions {
            justify-content:flex-start;
          }
          .rel-layout {
            grid-template-columns:minmax(260px,320px) minmax(0,1fr);
          }
          .rel-directory-screen {
            grid-template-columns:1fr;
          }
          .rel-side-stack {
            grid-column:1 / -1;
            display:grid;
            grid-template-columns:repeat(2,minmax(0,1fr));
          }
          .rel-directory-side {
            display:grid;
            grid-template-columns:repeat(2,minmax(0,1fr));
          }
        }
        @media (max-width:760px) {
          .rel-workbench {
            padding-bottom:0;
          }
          .rel-header {
            position:relative;
            padding:calc(12px + var(--status-bar-height, 28px)) 12px 12px;
            gap:14px;
          }
          .rel-brand {
            display:grid;
            grid-template-columns:auto minmax(0,1fr);
            gap:10px;
          }
          .rel-brand > div:last-child {
            min-width:0;
          }
          .rel-brand-icon {
            width:42px;
            height:42px;
          }
          .rel-back-btn {
            width:38px;
            padding:0;
            justify-content:center;
            font-size:0;
          }
          .rel-back-btn span { font-size:24px; }
          .rel-brand h1 {
            font-size:19px;
          }
          .rel-brand p {
            font-size:12px;
          }
          .rel-header-actions {
            display:grid;
            grid-template-columns:1fr 1fr;
          }
          .rel-primary-btn,
          .rel-success-btn {
            padding:0 10px;
          }
          .rel-layout {
            grid-template-columns:1fr;
            padding:12px;
            gap:12px;
          }
          .rel-directory-screen,
          .rel-records-screen {
            padding:12px;
            grid-template-columns:1fr;
          }
          .rel-directory-toolbar {
            grid-template-columns:1fr;
          }
          .rel-contact-head {
            display:none;
          }
          .rel-contact-row {
            grid-template-columns:repeat(2,minmax(0,1fr));
            gap:10px 12px;
            padding:12px;
          }
          .rel-contact-person {
            grid-column:1 / -1;
          }
          .rel-contact-type,
          .rel-contact-last,
          .rel-contact-count,
          .rel-contact-score {
            min-width:0;
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:8px;
            font-size:12px;
          }
          .rel-contact-type:before,
          .rel-contact-last:before,
          .rel-contact-count:before,
          .rel-contact-score:before {
            content:attr(data-label);
            color:#64748b;
            font-size:10px;
            font-weight:750;
          }
          .rel-contact-score { justify-self:stretch; }
          .rel-directory-side {
            display:flex;
          }
          .rel-people-panel {
            position:relative;
            top:auto;
            max-height:none;
          }
          .rel-person-list {
            max-height:min(360px,42dvh);
            overscroll-behavior:contain;
          }
          .rel-layout > *,
          .rel-directory-screen > *,
          .rel-record-panel,
          .rel-entry-main { min-width:0; }
          .rel-entry h3,
          .rel-entry p { overflow-wrap:anywhere; }
          .rel-record-panel {
            min-height:0;
            padding:14px;
          }
          .rel-record-head {
            flex-direction:column;
          }
          .rel-record-filters {
            width:100%;
            overflow-x:auto;
            flex-wrap:nowrap;
          }
          .rel-side-stack {
            display:flex;
          }
          .rel-donut-wrap,
          .rel-analysis-focus {
            grid-template-columns:1fr;
          }
          .rel-timeline {
            padding-left:28px;
          }
          .rel-timeline:before {
            left:10px;
          }
          .rel-entry-dot {
            left:-24px;
          }
          .rel-quality-row {
            grid-template-columns:92px 1fr 24px;
          }
          .rel-batch-bar {
            left:12px;
            right:12px;
            bottom:calc(82px + var(--safe-area-bottom, 0px));
            transform:none;
            flex-wrap:wrap;
          }
          .rel-batch-bar input { min-width:0; flex:1 1 110px; }
        }

        /* 与系统深色成长中心统一的关系工作台视觉 */
        .rel-workbench {
          background:
            radial-gradient(circle at 10% 0%, rgba(139,92,246,.13), transparent 28%),
            radial-gradient(circle at 88% 12%, rgba(45,212,191,.1), transparent 24%),
            #030712;
          color:#e2e8f0;
        }
        .rel-header {
          min-height:88px;
          padding:14px 20px;
          background:rgba(3,7,18,.92);
          border-bottom:1px solid rgba(167,139,250,.15);
          box-shadow:0 18px 48px rgba(0,0,0,.2);
        }
        .rel-back-btn {
          border-color:rgba(167,139,250,.2);
          border-radius:11px;
          background:rgba(76,29,149,.13);
          color:#ddd6fe;
          box-shadow:none;
        }
        .rel-back-btn:hover { border-color:rgba(196,181,253,.45); color:#f5f3ff; background:rgba(109,40,217,.18); }
        .rel-brand-icon {
          width:46px;
          height:46px;
          background:linear-gradient(145deg, rgba(139,92,246,.35), rgba(20,184,166,.18));
          color:#ede9fe;
          box-shadow:0 0 28px rgba(139,92,246,.2), inset 0 0 0 1px rgba(196,181,253,.2);
        }
        .rel-brand h1 { color:#f8fafc; font-size:19px; }
        .rel-brand p { color:#64748b; font-size:12px; margin-top:4px; }
        .rel-tabs {
          gap:6px;
          padding:5px;
          height:auto;
          border:1px solid rgba(148,163,184,.09);
          border-radius:14px;
          background:rgba(15,23,42,.58);
        }
        .rel-tabs button {
          height:34px;
          padding:0 13px;
          border:0;
          border-radius:10px;
          color:#64748b;
          font-size:12px;
        }
        .rel-tabs button.active {
          color:#f5f3ff;
          border:0;
          background:linear-gradient(135deg, rgba(124,58,237,.72), rgba(13,148,136,.58));
          box-shadow:0 8px 22px rgba(76,29,149,.24);
        }
        .rel-primary-btn,
        .rel-success-btn {
          height:38px;
          border-radius:11px;
          padding:0 14px;
          font-size:12px;
          box-shadow:none;
        }
        .rel-primary-btn { background:linear-gradient(135deg,#7c3aed,#4f46e5); }
        .rel-success-btn { background:linear-gradient(135deg,#0f766e,#0891b2); }
        .rel-pulse-strip {
          display:grid;
          grid-template-columns:repeat(4,minmax(0,1fr));
          gap:10px;
          max-width:1540px;
          margin:16px auto 0;
          padding:0 24px;
        }
        .rel-pulse-strip > div {
          min-height:72px;
          padding:12px 14px;
          border-radius:16px;
          border:1px solid rgba(148,163,184,.09);
          background:linear-gradient(145deg,rgba(15,23,42,.82),rgba(8,15,30,.72));
          box-shadow:inset 0 1px 0 rgba(255,255,255,.025);
        }
        .rel-pulse-strip > div.accent {
          border-color:rgba(45,212,191,.18);
          background:linear-gradient(145deg,rgba(13,148,136,.2),rgba(30,41,59,.72));
        }
        .rel-pulse-strip span,
        .rel-pulse-strip small { display:block; color:#64748b; font-size:10px; font-weight:750; }
        .rel-pulse-strip strong { display:inline-block; margin:4px 6px 0 0; color:#e2e8f0; font-size:24px; line-height:1; }
        .rel-pulse-strip .accent strong { color:#99f6e4; }
        .rel-layout,
        .rel-directory-screen,
        .rel-records-screen { gap:16px; padding-top:16px; }
        .rel-card {
          background:linear-gradient(155deg,rgba(15,23,42,.84),rgba(7,13,27,.88));
          border:1px solid rgba(148,163,184,.09);
          border-radius:18px;
          box-shadow:0 18px 48px rgba(0,0,0,.14), inset 0 1px 0 rgba(255,255,255,.025);
        }
        .rel-people-directory-card,
        .rel-record-panel-wide { min-height:0; }
        .rel-panel-title h2,
        .rel-card h2,
        .rel-record-head h2 { color:#e2e8f0; font-size:15px; }
        .rel-panel-title span,
        .rel-record-head p,
        .rel-person-meta,
        .rel-contact-row,
        .rel-contact-person em,
        .rel-entry-top,
        .rel-entry p,
        .rel-quality-row,
        .rel-legend div,
        .rel-legend-btn,
        .rel-week-stats span { color:#64748b; }
        .rel-search-row input,
        .rel-filter-select,
        .rel-sort-row select,
        .rel-directory-toolbar input,
        .rel-directory-toolbar select,
        .rel-directory-toolbar button,
        .rel-search-row button,
        .rel-sort-row button {
          border-color:rgba(148,163,184,.12);
          border-radius:10px;
          background:rgba(2,6,23,.48);
          color:#cbd5e1;
        }
        .rel-search-row input::placeholder,
        .rel-directory-toolbar input::placeholder { color:#475569; }
        .rel-search-row input:focus,
        .rel-filter-select:focus,
        .rel-sort-row select:focus {
          border-color:rgba(167,139,250,.48);
          box-shadow:0 0 0 3px rgba(139,92,246,.1);
        }
        .rel-person-row,
        .rel-contact-row {
          border-radius:12px;
          background:rgba(2,6,23,.18);
        }
        .rel-person-row:hover,
        .rel-contact-row:hover { background:rgba(30,41,59,.62); border-color:rgba(148,163,184,.1); }
        .rel-person-row.active,
        .rel-contact-row.active {
          background:linear-gradient(90deg,rgba(124,58,237,.18),rgba(13,148,136,.08));
          border-color:rgba(167,139,250,.28);
          box-shadow:inset 3px 0 0 #8b5cf6;
        }
        .rel-person-name strong,
        .rel-contact-person strong,
        .rel-entry h3,
        .rel-legend strong,
        .rel-quality-row strong,
        .rel-week-stats strong { color:#e2e8f0; }
        .rel-score { background:rgba(51,65,85,.54); color:#94a3b8; }
        .rel-score.good { color:#6ee7b7; background:rgba(16,185,129,.12); }
        .rel-score.mid { color:#fcd34d; background:rgba(245,158,11,.12); }
        .rel-score.low { color:#94a3b8; background:rgba(100,116,139,.14); }
        .rel-record-head,
        .rel-directory-toolbar,
        .rel-contact-head,
        .rel-entry { border-color:rgba(148,163,184,.08); }
        .rel-record-filters button,
        .rel-person-chip,
        .rel-full-btn,
        .rel-quick-list button {
          border-color:rgba(148,163,184,.1);
          background:rgba(2,6,23,.36);
          color:#94a3b8;
          border-radius:10px;
        }
        .rel-record-filters button.active {
          color:white;
          border-color:transparent;
          background:linear-gradient(135deg,#7c3aed,#0f766e);
        }
        .rel-link-btn { color:#5eead4; }
        .rel-timeline:before { background:rgba(148,163,184,.12); }
        .rel-entry-dot { background:#8b5cf6; border-color:#1e1b4b; box-shadow:0 0 0 1px rgba(167,139,250,.45),0 0 18px rgba(139,92,246,.3); }
        .rel-tag.blue { color:#7dd3fc; background:rgba(14,165,233,.12); }
        .rel-tag.green { color:#6ee7b7; background:rgba(16,185,129,.12); }
        .rel-tag.orange { color:#fdba74; background:rgba(249,115,22,.12); }
        .rel-tag.purple { color:#c4b5fd; background:rgba(139,92,246,.14); }
        .rel-donut > div { background:#0b1220; box-shadow:inset 0 0 0 1px rgba(148,163,184,.1); }
        .rel-donut strong { color:#f8fafc; }
        .rel-donut span { color:#64748b; }
        .rel-quality-row div,
        .rel-analysis-line div { background:rgba(51,65,85,.62); }
        .rel-quick-list strong { color:#e2e8f0; }
        .rel-quick-list span { color:#64748b; }
        .rel-analysis-focus {
          background:linear-gradient(145deg,rgba(76,29,149,.18),rgba(8,47,73,.16));
          border-color:rgba(167,139,250,.12);
          border-radius:14px;
        }
        .rel-analysis-line { color:#94a3b8; }
        .rel-empty { border-color:rgba(148,163,184,.14); background:rgba(2,6,23,.3); color:#64748b; border-radius:14px; }
        .rel-graph-screen { padding:16px 24px 24px; background:transparent; min-height:0; max-width:1540px; margin:0 auto; }
        @media (max-width:760px) {
          .rel-header { padding:calc(12px + var(--status-bar-height, 28px)) 12px 12px; }
          .rel-pulse-strip { grid-template-columns:repeat(2,minmax(0,1fr)); padding:0 12px; margin-top:12px; }
          .rel-pulse-strip > div { min-height:66px; padding:10px 12px; }
          .rel-pulse-strip strong { font-size:21px; }
          .rel-tabs { width:100%; max-width:100%; min-width:0; overflow-x:auto; justify-content:flex-start; scrollbar-width:none; overscroll-behavior-x:contain; -webkit-overflow-scrolling:touch; }
          .rel-tabs::-webkit-scrollbar { display:none; }
          .rel-tabs button { flex:0 0 auto; }
          .rel-layout,
          .rel-directory-screen,
          .rel-records-screen,
          .rel-graph-screen { padding:12px; }
          .rel-layout.is-analysis .rel-people-panel { display:none; }
        }
      `}</style>
    </div>
  );
};

export default PeopleList;
